import { z } from 'zod/v4'

export const PatientStatusValidator = z.enum(['New Patient', 'Existing Patient'])
export const QualificationStatusValidator = z.enum(['Qualified', 'Unqualified'])
export const UnqualifiedReasonValidator = z.enum([
  'Abandoned',
  'Missed Call',
  'Silent Caller',
  'Wrong Number',
  'Medicaid',
  'Unavailable Service',
  'Follow Up',
  'Reschedule',
  'Cancellation',
  'Payment Question',
  'Other',
])
export const ConversionStatusValidator = z.enum(['Converted', 'Unconverted'])
export const UnconvertedReasonValidator = z.enum([
  'Insurance',
  'Scheduling',
  'Still Considering',
  'Other',
])
export const PrimaryConcernValidator = z.enum([
  'Damanged Teeth',
  'Dentures',
  'Emergency',
  'Extraction',
  'General',
  'Implant',
  'Pain',
  'Smile Restoration',
  'Veneers',
  'Other',
])
export const TimeOfDayValidator = z.enum(['Business Hours', 'After Hours'])

export const newCallOrderedValidators = [
  PatientStatusValidator,
  QualificationStatusValidator,
  TimeOfDayValidator,
  PrimaryConcernValidator,
] as const

export const AppointmentValidator = z.object({
  number_of_appointments: z.number().int().positive(),
  date: z.string(),
  time: z.string(),
  day: z.enum(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']),
  reason: z.string(),
})

export const TranscriptDataValidator = z.object({
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  value: z.number().optional(),
  tags: z.array(z.string()),
  call_summary: z.string(),
  appointments: z
    .object({
      number_of_appointments: z.number(),
      date: z.string(),
      time: z.string(),
      day: z.string(),
      reason: z.string(),
    })
    .optional(),
})

export const EnrichedCallValidator = z.object({
  id: z.string(),
  recording: z.string().nullable(),
  answered: z.boolean(),
  business_phone_number: z.string(),
  customer_city: z.string().nullable(),
  customer_country: z.string().nullable(),
  customer_name: z.string(),
  customer_phone_number: z.string(),
  customer_state: z.string().nullable(),
  recording_duration: z.number().nullable(),
  recording_player: z.string().nullable(),
  start_time: z.string(),
  tracking_phone_number: z.string(),
  voicemail: z.boolean(),
  direction: z.string(),
  duration: z.number(),
  call_type: z.string(),
  company_id: z.string(),
  company_name: z.string(),
  created_at: z.string(),
  first_call: z.boolean(),
  formatted_value: z.string().optional(),
  lead_status: z.string().optional().nullable(),
  source: z.string().optional(),
  source_name: z.string().optional(),
  medium: z.string().optional(),
  keywords: z.string().optional().nullable(),
  campaign: z.string().optional().nullable(),
  referring_url: z.string().optional().nullable(),
  landing_page_url: z.string().optional().nullable(),
  last_requested_url: z.string().optional().nullable(),
  refferer_domain: z.string().optional(),
  utm_source: z.string().optional().nullable(),
  utm_medium: z.string().optional().nullable(),
  utm_term: z.string().optional().nullable(),
  utm_campaign: z.string().optional().nullable(),
  utma: z.string().optional().nullable(),
  utmb: z.string().optional().nullable(),
  utmc: z.string().optional().nullable(),
  utmv: z.string().optional().nullable(),
  utmz: z.string().optional().nullable(),
  ga: z.string().optional().nullable(),
  gclid: z.string().optional().nullable(),
  fbclid: z.string().optional().nullable(),
  msclkid: z.string().optional().nullable(),
  formatted_customer_phone_number: z.string(),
  tags: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        tag_level: z.string().optional(),
        color: z.string().optional(),
        background_color: z.string().optional(),
        company_id: z.string().optional(),
        status: z.string().optional(),
        disabled: z.boolean().optional(),
        created_at: z.string().optional(),
      }),
    )
    .optional()
    .nullable(),
})

export const CallRailPaginatedResponseValidator = z.object({
  page: z.number(),
  per_page: z.number(),
  total_pages: z.number(),
  total_records: z.number(),
  calls: z.array(EnrichedCallValidator),
})

export const BaseCallValidator = z.object({
  recording: z.string().optional(),
  resource_id: z.string(),
})

export const WebhookCallValidator = z.object({
  recording: z.string().nullable(),
  resource_id: z.string().optional(),
  id: z.coerce.string().optional(),
  answered: z.boolean().optional(),
  changes: z.array(z.string()).optional(),
  company_id: z.union([z.string(), z.number()]).optional(),
  company_resource_id: z.string().optional(),
  duration: z.coerce.string().optional(),
  customer_phone_number: z.string(),
  gclid: z.string().optional().nullable(),
  campaign: z.string().optional().nullable(),
  source_name: z.string().optional(),
  source: z.string().optional(),
  speaker_percent: z
    .union([z.string(), z.object({ agent: z.number(), customer: z.number() })])
    .optional(),
  start_time: z.string().optional(),
  waveforms: z.union([z.string(), z.array(z.string())]).optional(),
  customer_name: z.string().optional(),
})

export const SalesforceLeadValidator = z.object({
  campaign: z.string().nullable().optional(),
  name: z.string(),
  email: z.string().max(80).optional(),
  firstName: z.string().max(40),
  lastName: z.string().max(40),
  notes: z.string().optional(),
  phone: z.string().max(15),
  transcriptId: z.string().max(255).optional(),
  companyId: z.string(),
  value: z.number().optional(),
})

export type TranscriptData = z.infer<typeof TranscriptDataValidator>
export type EnrichedCall = z.infer<typeof EnrichedCallValidator>
export type WebhookCall = z.infer<typeof WebhookCallValidator>
export type SalesforceLead = z.infer<typeof SalesforceLeadValidator>
